import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { Budgets } from "../../features/billing/budgets";
import { BillingLayout, type BillingTab } from "../../features/billing/layout";
import { Notifications } from "../../features/billing/notifications";
import { Overview } from "../../features/billing/overview";
import { Transactions } from "../../features/billing/transactions";
import { SpotUsagePage } from "../../features/spot/usage";
import { Usage } from "../../features/billing/usage";
import { BillingDetails } from "../../features/billing/details";

// The eager route guard uses tab names only, so page components remain in the lazy route chunk.
const TABS: Record<BillingTab, true> = { overview: true, usage: true, transactions: true, budgets: true };

const PAGES: Record<BillingTab, (p: { org: string }) => React.ReactNode> = {
  overview: Overview,
  usage: Usage,
  transactions: Transactions,
  budgets: ({ org }) => (
    <>
      <Budgets org={org} />
      <BillingDetails title="Email notifications" description="Manage low-balance alerts, budget emails, and billing recipients.">
        <Notifications org={org} />
      </BillingDetails>
    </>
  ),
};

/** Tabs folded into another keep their URLs: Credits into Balance, Receipts into Transactions, Alerts into Budgets. */
const MOVED: Record<string, BillingTab> = { credits: "overview", receipts: "transactions", notifications: "budgets" };

export const Route = createFileRoute("/_authed/$org/billing/$tab")({
  beforeLoad: ({ params }) => {
    const moved = MOVED[params.tab];
    if (moved) throw redirect({ href: `/${encodeURIComponent(params.org)}/billing/${moved}`, replace: true });
    if (!Object.hasOwn(TABS, params.tab)) throw notFound();
  },
  // Checkout and card-setup returns and the budget sheet carry their state in the query string (ui-spec §6.14.1).
  validateSearch: (s: Record<string, unknown>) =>
    s as Record<string, string | boolean | undefined>,
  component: BillingPage,
});

function BillingPage() {
  const { org, tab } = Route.useParams();
  const Page = PAGES[tab as BillingTab];
  const { instances } = Route.useSearch();
  return (
    <BillingLayout org={org} tab={tab as BillingTab}>
      {tab === "usage" && (instances === true || instances === "true") ? <SpotUsagePage org={org} /> : <Page org={org} />}
    </BillingLayout>
  );
}
